// Login.tsx — public login page on react-bootstrap.
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import { BsBoxArrowInRight } from 'react-icons/bs'
import Text from 'common/ui/Text'
import { GitHubIcon, GoogleIcon } from './icons'

const Login = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const returnTo = searchParams.get('return_to') || ''

    useEffect(() => {
        const errorMsg = searchParams.get('error')
        if (errorMsg) {
            setError(decodeURIComponent(errorMsg))
        }
    }, [searchParams])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
                credentials: 'include',
            })

            if (!res.ok) {
                const data = await res.json()
                throw new Error(data.error || 'Login failed')
            }

            const data = await res.json()
            localStorage.setItem('identity_token', data.token)

            if (returnTo) {
                window.location.href = returnTo
            } else {
                navigate('/dashboard')
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'An unexpected error occurred')
        } finally {
            setLoading(false)
        }
    }

    const handleSocialLogin = (provider: string) => {
        const redirectUrl = returnTo
            ? `/api/auth/${provider}?return_to=${encodeURIComponent(returnTo)}`
            : `/api/auth/${provider}`
        window.location.href = redirectUrl
    }

    return (
        <div className="auth-wrapper">
            <Card style={{ width: '420px', maxWidth: '100%' }}>
                <Card.Body>
                    <h2 className="text-center mb-4">Log In</h2>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <div className="d-flex flex-column gap-2 mb-3">
                        <Button
                            variant="github"
                            onClick={() => handleSocialLogin('github')}
                            className="d-flex align-items-center justify-content-center gap-2"
                        >
                            <GitHubIcon /> Log in with GitHub
                        </Button>
                        <Button
                            variant="google"
                            onClick={() => handleSocialLogin('google')}
                            className="d-flex align-items-center justify-content-center gap-2"
                        >
                            <GoogleIcon /> Log in with Google
                        </Button>
                    </div>

                    <div className="text-center text-muted my-3">OR</div>

                    <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
                        <Form.Group controlId="login-email">
                            <Form.Label>Email</Form.Label>
                            <Text
                                type="email"
                                style={{ width: "100%" }}
                                required
                                value={email}
                                placeholder="Enter email"
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                            />
                        </Form.Group>
                        <Form.Group controlId="login-password">
                            <Form.Label>Password</Form.Label>
                            <Text
                                type="password"
                                style={{ width: "100%" }}
                                required
                                value={password}
                                placeholder="Password"
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit" disabled={loading} className="d-flex align-items-center justify-content-center gap-2">
                            <BsBoxArrowInRight /> {loading ? 'Logging in…' : 'Log In'}
                        </Button>
                    </Form>

                    <div className="text-center mt-3 text-muted">
                        <span>Need an account? </span>
                        <Button
                            variant="link"
                            className="p-0 align-baseline"
                            onClick={() => navigate('/register' + (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''))}
                        >
                            Sign Up
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default Login
